import { ReactNode } from 'react';

import styles from './CustomNavItem.module.scss';
import { SidebarItems } from 'enums';
import { clsx } from 'clsx';

interface ICustomNavItemProps {
  title: string;
  icon: ReactNode;
  onSelect: () => void;
  /** The item the current route belongs to, or undefined off-nav. */
  selectedItem?: SidebarItems;
  /**
   * Optional count of items needing attention, shown as a pill.
   *
   * Hidden at zero rather than rendered as "0": a badge that is always present
   * stops being a signal, and people learn to read past it — which is exactly
   * what you cannot afford for a queue that gates providers from working.
   */
  badgeCount?: number;
}

export const CustomNavItem = ({
  title,
  icon,
  onSelect,
  selectedItem,
  badgeCount,
}: ICustomNavItemProps) => {
  const showBadge = Boolean(badgeCount && badgeCount > 0);
  const isActive = selectedItem === title;

  return (
    // A real button: the row was a bare div, so it could not be reached by
    // keyboard and announced as nothing at all. `aria-current` is what tells a
    // screen reader which page you are on — the cyan bar only says it visually.
    <button
      type="button"
      className={styles.container}
      onClick={() => onSelect()}
      aria-current={isActive ? 'page' : undefined}
    >
      {/* Fixed-width slot, so every label in the list starts on the same x
          regardless of how wide the individual icon's artwork happens to be. */}
      <span className={styles.iconSlot}>{icon}</span>
      <span className={clsx(styles.text, isActive && styles.active)}>{title}</span>
      {showBadge && (
        <span
          className={styles.badge}
          // Screen readers get the meaning; the pill alone reads as a bare
          // number sitting next to a link.
          aria-label={`${badgeCount} awaiting approval`}
        >
          {badgeCount! > 99 ? '99+' : badgeCount}
        </span>
      )}
      {isActive && <span className={styles.highlighting} />}
    </button>
  );
};
